import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../AuthContext';
import { BellAlertIcon } from '@heroicons/react/20/solid';

export default function SpeakUp() {
    const location = useLocation();
    const navigate = useNavigate();
    const user = useContext(AuthContext);

    const [details, setDetails] = useState({
        perpetratorName: '',
        dateOfIncident: '',
        timeOfIncident: '',
        locationOfIncident: '',
        descriptionOfIncident: '',
        policyViolated: '',
        witnesses: [],
        escalateToHR: 'Yes',
        anonymous: 'No',
        StaffId: '',
    });

    const [staff, setStaff] = useState([]);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [showButton, setShowButton] = useState(false);

    useEffect(() => {
        const fetchStaff = async () => {
            try {
                const response = await fetch('https://api.insightos.co.uk/api/staff', {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                });
                if (response.ok) {
                    const data = await response.json();
                    setStaff(data.data);
                } else {
                    console.error('Failed to fetch staff data');
                }
            } catch (error) {
                console.error('Error fetching staff data:', error);
            }
        };
        fetchStaff();
    }, []);

    useEffect(() => {
        const getWitnessStatementRequest = async () => {
            try {
                const response = await fetch('https://api.insightos.co.uk/api/witnessstatement', {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                });
                if (response.ok) {
                    const data = await response.json();
                    const witnessstatement = data.data;
                    setShowButton(witnessstatement.length > 0);
                } else {
                    console.error('Failed to fetch witness statement data');
                }
            } catch (error) {
                console.error('Error fetching witness statement data:', error);
            }
        }

        getWitnessStatementRequest();

    }, []);



    const handleChange = (e) => {
        const { name, value, options, type } = e.target;
        if (name === 'witnesses') {
            const selectedOptions = Array.from(options)
                .filter(option => option.selected)
                .map(option => option.value);
            setDetails(prev => ({ ...prev, witnesses: selectedOptions }));
        } else {
            setDetails(prev => ({ ...prev, [name]: value }));
        }
    };

    const handlePerpetratorChange = (e) => {
        const selectedId = e.target.value;
        const selectedPerson = staff.find(person => String(person.id) === selectedId);
        setDetails(prev => ({
            ...prev,
            perpetratorName: selectedPerson ? selectedPerson.Name : '',
            StaffId: selectedId
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const form = {
            StaffName: details.perpetratorName,
            DateOfIncident: details.dateOfIncident,
            TimeOfIncident: details.timeOfIncident,
            LocationOfIncident: details.locationOfIncident,
            DescriptionOfIncident: details.descriptionOfIncident,
            PolicyViolated: details.policyViolated,
            Witnesses: details.witnesses,
            HR: details.escalateToHR,
            Anon: details.anonymous,
            Author: user?.name || '',
            StaffId: details.StaffId,
        };

        try {
            const response = await fetch('https://api.insightos.co.uk/api/speakup', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(form),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setSubmitStatus('success');
                setDetails({
                    perpetratorName: '',
                    dateOfIncident: '',
                    timeOfIncident: '',
                    locationOfIncident: '',
                    descriptionOfIncident: '',
                    policyViolated: '',
                    witnesses: [],
                    escalateToHR: 'Yes',
                    anonymous: 'No',
                    StaffId: '',
                });
            } else {
                setSubmitStatus('error');
            }
        } catch (error) {
            setSubmitStatus('error');
            console.error('Submission error:', error);
        }
    };

    return (
        <>
            {showButton && (
                <div className="fixed top-4 right-4 z-50">
                    <button
                        data-component="speakup-floating-action"
                        data-bucket="communication"
                        data-feature="witness-statement"
                        onClick={() => navigate('/witnessstatement')}
                        className="bg-red-500 animate-bounce text-white px-4 py-2 rounded-lg shadow-lg hover:from-pink-600 hover:to-purple-600 transition-all duration-200"
                    >
                        <BellAlertIcon className="h-5 w-5 inline-block mr-1" aria-hidden="true" />
                    </button>
                </div>
            )}
        
        <div data-component="speakup-page" className="min-h-screen bg-gradient-to-br from-purple-100 via-indigo-100 to-pink-100 flex items-center justify-center py-8 px-2">
            <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl p-8 border border-purple-200">
                <h1 className="text-3xl font-extrabold mb-2 text-purple-700 font-bebas tracking-wide text-center drop-shadow">
                    Speak Up
                </h1>
                <p className="mb-4 text-center text-gray-700 font-medium">
                    Please fill out the form below to report any incidents.
                </p>
                <div className="mb-6 p-4 rounded-lg bg-gradient-to-r from-pink-200 via-purple-200 to-indigo-200 border-l-4 border-purple-500 text-purple-900 text-sm shadow">
                    <strong>Notice:</strong> This reporting form is provided for employees to raise genuine concerns or grievances within the workplace. You may choose to submit your report anonymously; however, all information must be accurate, factual, and submitted in good faith.<br /><br />
                    The submission of false, misleading, or malicious statements is a serious offence and may result in disciplinary action, up to and including dismissal, in accordance with company policy and UK employment law.<br /><br />
                    All reports will be treated confidentially and investigated appropriately by the HR department or designated personnel.
                </div>
                <form data-component="speakup-form" onSubmit={handleSubmit} className="space-y-6">
                    {/* Name of Perpetrator */}
                    <div>
                        <label className="block text-sm font-semibold mb-2 text-purple-700">
                            Name of Perpetrator
                        </label>
                        <select
                            data-component="speakup-field"
                            data-bucket="communication"
                            data-feature="perpetrator"
                            name="perpetratorName"
                            value={details.StaffId}
                            onChange={handlePerpetratorChange}
                            className="w-full border-2 border-purple-200 rounded-lg shadow focus:ring-2 focus:ring-purple-400 focus:border-purple-400 px-3 py-2 bg-white text-gray-700"
                        >
                            <option value="">Select a name</option>
                            {staff.map((person) => (
                                <option key={person.id || person.Name} value={person.id}>
                                    {person.Name}
                                </option>
                            ))}
                        </select>
                    </div>
                    {/* Date of Incident */}
                    <div className="flex flex-col md:flex-row md:space-x-6">
                        <div className="flex-1 mb-4 md:mb-0">
                            <label className="block text-sm font-semibold mb-2 text-purple-700">
                                Date of Incident
                            </label>
                            <input
                                data-component="speakup-field"
                                data-bucket="communication"
                                data-feature="date"
                                type="date"
                                name="dateOfIncident"
                                value={details.dateOfIncident}
                                onChange={handleChange}
                                className="w-full border-2 border-purple-200 rounded-lg shadow focus:ring-2 focus:ring-purple-400 focus:border-purple-400 px-3 py-2 bg-white text-gray-700"
                            />
                        </div>
                        {/* Time of Incident */}
                        <div className="flex-1">
                            <label className="block text-sm font-semibold mb-2 text-purple-700">
                                Time of Incident
                            </label>
                            <input
                                data-component="speakup-field"
                                data-bucket="communication"
                                data-feature="time"
                                type="time"
                                name="timeOfIncident"
                                value={details.timeOfIncident}
                                onChange={handleChange}
                                className="w-full border-2 border-purple-200 rounded-lg shadow focus:ring-2 focus:ring-purple-400 focus:border-purple-400 px-3 py-2 bg-white text-gray-700"
                            />
                        </div>
                    </div>
                    {/* Location of Incident */}
                    <div>
                        <label className="block text-sm font-semibold mb-2 text-purple-700">
                            Location of Incident
                        </label>
                        <input
                            data-component="speakup-field"
                            data-bucket="communication"
                            data-feature="location"
                            type="text"
                            name="locationOfIncident"
                            value={details.locationOfIncident}
                            onChange={handleChange}
                            className="w-full border-2 border-purple-200 rounded-lg shadow focus:ring-2 focus:ring-purple-400 focus:border-purple-400 px-3 py-2 bg-white text-gray-700"
                            placeholder="Enter location"
                        />
                    </div>
                    {/* Description of Incident */}
                    <div>
                        <label className="block text-sm font-semibold mb-2 text-purple-700">
                            Description of Incident
                        </label>
                        <textarea
                            data-component="speakup-field"
                            data-bucket="communication"
                            data-feature="description"
                            name="descriptionOfIncident"
                            value={details.descriptionOfIncident}
                            onChange={handleChange}
                            className="w-full border-2 border-pink-200 rounded-lg shadow focus:ring-2 focus:ring-pink-400 focus:border-pink-400 px-3 py-3 bg-white text-gray-700 min-h-[120px] resize-vertical"
                            placeholder="Provide a detailed description"
                        ></textarea>
                    </div>
                    {/* Policy Violated */}
                    <div>
                        <label className="block text-sm font-semibold mb-2 text-purple-700">
                            Policy/Rule Violated
                        </label>
                        <textarea
                            data-component="speakup-field"
                            data-bucket="communication"
                            data-feature="policy"
                            name="policyViolated"
                            value={details.policyViolated}
                            onChange={handleChange}
                            className="w-full border-2 border-indigo-200 rounded-lg shadow focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 px-3 py-3 bg-white text-gray-700 min-h-[60px] resize-vertical"
                            placeholder="Mention specific policy or rule"
                        ></textarea>
                    </div>
                    {/* Witness Information - MULTIPLE SELECT */}
                    <div>
                        <label className="block text-sm font-semibold mb-2 text-purple-700">
                            Witnesses
                        </label>
                        <select
                            data-component="speakup-field"
                            data-bucket="communication"
                            data-feature="witnesses"
                            name="witnesses"
                            multiple
                            value={details.witnesses}
                            onChange={handleChange}
                            className="w-full border-2 border-purple-200 rounded-lg shadow focus:ring-2 focus:ring-purple-400 focus:border-purple-400 px-3 py-2 bg-white text-gray-700 h-32"
                        >
                            {staff.map((person) => (
                                <option key={person.id || person.Name} value={person.Name}>
                                    {person.Name}
                                </option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                            Hold <span className="font-semibold text-purple-600">Ctrl</span> (Windows) or <span className="font-semibold text-purple-600">Cmd</span> (Mac) to select multiple witnesses.
                        </p>
                    </div>
                    {/* Escalate to HR */}
                    <div>
                        <label className="block text-sm font-semibold mb-2 text-purple-700">
                            Escalate to HR?
                        </label>
                        <select
                            data-component="speakup-field"
                            data-bucket="communication"
                            data-feature="escalate"
                            name="escalateToHR"
                            value={details.escalateToHR}
                            onChange={handleChange}
                            className="w-full border-2 border-purple-200 rounded-lg shadow focus:ring-2 focus:ring-purple-400 focus:border-purple-400 px-3 py-2 bg-white text-gray-700"
                        >
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                        </select>
                    </div>
                    {/* Anonymous */}
                    <div>
                        <label className="block text-sm font-semibold mb-2 text-purple-700">
                            Do you wish to remain anonymous?
                        </label>
                        <select
                            data-component="speakup-field"
                            data-bucket="communication"
                            data-feature="anonymous"
                            name="anonymous"
                            value={details.anonymous}
                            onChange={handleChange}
                            className="w-full border-2 border-purple-200 rounded-lg shadow focus:ring-2 focus:ring-purple-400 focus:border-purple-400 px-3 py-2 bg-white text-gray-700"
                        >
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                        </select>
                    </div>
                    {/* Submit Button */}
                    <div className="flex justify-center">
                        <button
                            data-component="speakup-action"
                            data-bucket="communication"
                            data-feature="submit"
                            type="submit"
                            className="bg-gradient-to-r from-purple-500 via-pink-500 to-indigo-500 text-white px-8 py-3 rounded-xl font-bold shadow-lg hover:from-pink-600 hover:to-purple-600 transition-all duration-200"
                        >
                            Submit
                        </button>
                    </div>
                    {submitStatus === 'success' && (
                        <div className="text-green-700 bg-green-100 border border-green-300 rounded-lg px-4 py-2 mt-2 text-center font-semibold shadow">
                            Report submitted successfully.
                        </div>
                    )}
                    {submitStatus === 'error' && (
                        <div className="text-red-700 bg-red-100 border border-red-300 rounded-lg px-4 py-2 mt-2 text-center font-semibold shadow">
                            There was an error submitting your report.
                        </div>
                    )}
                </form>
            </div>
        </div>
        </>
    );
}